import React, { useEffect, useState, useRef } from 'react';
import { collection, query, where, getDocs, updateDoc, doc, addDoc, serverTimestamp, onSnapshot, QuerySnapshot, DocumentData } from 'firebase/firestore';
import { getFailoverAwareDatabase, getFailoverAwareAuth } from '../../config/firebaseWithFailover';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import styles from '../carrier/Notifications.module.css';
import { CARRIER_ACTION_BUTTON_TEXT_STYLE } from '../../constants/carrierActionButtonText';
import RejectionOptionsModal from '../../components/shipper/RejectionOptionsModal';
import CounterOfferModal from '../../components/shipper/CounterOfferModal';
import { updateLoadRequestStatus, handleShipperRejection } from '../../services/notificationService';
import { Dialog } from '@reach/dialog';
import '@reach/dialog/styles.css';
import Logger from '../../services/logger';
import { NOTIFICATION_STATUS, NOTIFICATION_TYPES, PARTNER_REQUEST_STATUS, DASHBOARD_ACTION_REQUIRED_NOTIFICATION_TYPES } from '../../constants/notificationTypes';
import {
  NOTIFICATION_TRAY_LOAD_MORE_LABEL,
  filterNotificationsForBellTray,
} from '../../constants/notificationTrayUi';
import { SHIPPING_SCHEDULE_STATUS } from '../../constants/statusValues';
import { getShipperPurchaseOrderRecordForWorkflow } from '../../services/purchaseOrderFirestoreQueries';
import { withAssignPoQuery } from '../../utils/poCarrierPartnerNavState';
import { reconcileStalePoLinkedTrayNotifications } from '../../services/notificationFirestoreQueries';
import { cleanupExpiredBellTrayNotifications } from '../../services/notificationTrayRetention';
import { useNotificationTrayPagination } from '../../hooks/useNotificationTrayPagination';

interface Notification {
  id: string;
  type: string;
  senderId: string;
  senderName: string;
  message: string;
  read: boolean;
  createdAt: Date;
  requiresAction?: boolean;
  loadDetails?: {
    pickupLocation: {
      address: string;
      city: string;
      state: string;
    };
    deliveryLocation: {
      address: string;
      city: string;
      state: string;
    };
    rate: number;
    poNumber?: string;
    isMarketplace?: boolean;
  };
  shipperId?: string;
  carrierId?: string;
  poNumber?: string;
}

interface ShipperNotificationsTrayProps {
  onClose: () => void;
}

const ShipperNotificationsTray: React.FC<ShipperNotificationsTrayProps> = ({ onClose }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showCounterOfferModal, setShowCounterOfferModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const trayRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsNotification, setDetailsNotification] = useState<Notification | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const { visibleCount, hasMore, remaining, loadMore, pageSize } = useNotificationTrayPagination(notifications.length);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !showDetailsModal &&
        !showRejectionModal &&
        !showCounterOfferModal &&
        trayRef.current &&
        !trayRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, showDetailsModal, showRejectionModal, showCounterOfferModal]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getFailoverAwareAuth(), async (user) => {
      if (user) {
        setCurrentUser(user);
        try {
          void cleanupExpiredBellTrayNotifications(
            getFailoverAwareDatabase(),
            user.uid,
            ['shipperId', 'userId']
          ).catch((err) => {
            Logger.warn('[ShipperNotificationsTray] Retention cleanup skipped', err as Error);
          });

          // Query notifications for shipper using shipperId
          const shipperQuery = query(
            collection(getFailoverAwareDatabase(), 'notifications'),
            where('shipperId', '==', user.uid)
          );
          
          // Query notifications for shipper using userId (fallback)
          const userQuery = query(
            collection(getFailoverAwareDatabase(), 'notifications'),
            where('userId', '==', user.uid)
          );
          
          let shipperNotifications: Notification[] = [];
          let userNotifications: Notification[] = [];
          let shipperUnsubscribe: (() => void) | null = null;
          let userUnsubscribe: (() => void) | null = null;
          
          const processNotifications = () => {
            // Merge and deduplicate notifications
            const allNotifications = [...shipperNotifications, ...userNotifications];
            const uniqueNotifications = allNotifications.filter((notification, index, self) => 
              index === self.findIndex(n => n.id === notification.id)
            );
            
            // Exclude dashboard Action Required + items older than 4 weeks (shared C/B/S filter).
            const bellOnlyNotifications = filterNotificationsForBellTray(uniqueNotifications, 'shipper').sort(
              (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
            );

            Logger.debug('[DEBUG] Shipper notifications processed:', {
              shipperNotifications: shipperNotifications.length,
              userNotifications: userNotifications.length,
              total: uniqueNotifications.length,
              bellOnly: bellOnlyNotifications.length,
              filteredOut: uniqueNotifications.length - bellOnlyNotifications.length,
              types: uniqueNotifications.map(n => n.type),
              bellTypes: bellOnlyNotifications.map(n => n.type)
            });
            setNotifications(bellOnlyNotifications);
            void reconcileStalePoLinkedTrayNotifications(
              getFailoverAwareDatabase(),
              bellOnlyNotifications
            ).catch((staleErr) => {
              Logger.warn(
                '[ShipperNotificationsTray] Stale PO tray reconcile failed (non-fatal)',
                staleErr as Error
              );
            });
            setLoading(false);
            setError(null);
          };
          
          shipperUnsubscribe = onSnapshot(shipperQuery, (snapshot: QuerySnapshot<DocumentData>) => {
            try {
              const notificationList: Notification[] = [];
              snapshot.forEach((doc) => {
                try {
                  const data = doc.data();
                  notificationList.push({
                    id: doc.id,
                    type: data.type || 'unknown',
                    senderId: data.senderId || data.carrierId || '',
                    senderName: data.senderName || 'Unknown',
                    message: data.message || '',
                    read: data.read || false,
                    createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
                    requiresAction: data.requiresAction || false,
                    loadDetails: data.loadDetails || null,
                    shipperId: data.shipperId || undefined,
                    carrierId: data.carrierId || undefined
                  });
                } catch (docError) {
                  Logger.error('[ShipperNotificationsTray] Error processing notification document:', docError as Error, { docId: doc.id });
                }
              });
              shipperNotifications = notificationList;
              processNotifications();
            } catch (snapshotError) {
              Logger.error('[ShipperNotificationsTray] Error processing shipper notification snapshot:', snapshotError as Error);
              setError('Failed to load notifications');
              setLoading(false);
            }
          });

          userUnsubscribe = onSnapshot(userQuery, (snapshot: QuerySnapshot<DocumentData>) => {
            try {
              const notificationList: Notification[] = [];
              snapshot.forEach((doc) => {
                try {
                  const data = doc.data();
                  notificationList.push({
                    id: doc.id,
                    type: data.type || 'unknown',
                    senderId: data.senderId || data.carrierId || '',
                    senderName: data.senderName || 'Unknown',
                    message: data.message || '',
                    read: data.read || false,
                    createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
                    requiresAction: data.requiresAction || false,
                    loadDetails: data.loadDetails || null,
                    shipperId: data.shipperId || undefined,
                    carrierId: data.carrierId || undefined
                  });
                } catch (docError) {
                  Logger.error('[ShipperNotificationsTray] Error processing notification document:', docError as Error, { docId: doc.id });
                }
              });
              userNotifications = notificationList;
              processNotifications();
            } catch (snapshotError) {
              Logger.error('[ShipperNotificationsTray] Error processing user notification snapshot:', snapshotError as Error);
              setError('Failed to load notifications');
              setLoading(false);
            }
          });

          return () => {
            if (shipperUnsubscribe) shipperUnsubscribe();
            if (userUnsubscribe) userUnsubscribe();
          };
        } catch (error) {
          Logger.error('[ShipperNotificationsTray] Error setting up notifications:', error as Error);
          setError('Failed to load notifications');
          setLoading(false);
        }
      } else {
        setCurrentUser(null);
        setNotifications([]);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleMarkAsRead = async (notificationId: string) => {
    const notificationRef = doc(getFailoverAwareDatabase(), 'notifications', notificationId);
    await updateDoc(notificationRef, { read: true });
    setNotifications(prev => prev.map(n => 
      n.id === notificationId ? { ...n, read: true } : n
    ));
  };

  const handleNotificationClick = (notification: Notification) => {
    if ((notification.type === 'carrier_reject' || notification.type === 'offer_rejected') && notification.requiresAction) {
      setSelectedNotification(notification);
      setShowRejectionModal(true);
    } else if (notification.type === 'carrier_counter_offer' && notification.requiresAction) {
      setSelectedNotification(notification);
      setShowCounterOfferModal(true);
    } else if (notification.type === 'make_offer' && notification.requiresAction) {
      // Marketplace offers with action required - show accept/reject modal
      setSelectedNotification(notification);
      setShowCounterOfferModal(true);
    } else if (notification.type === 'carrier_accept' && notification.requiresAction) {
      // Marketplace carrier acceptance - process directly
      setSelectedNotification(notification);
      handleAcceptCounterOffer();
    } else if (notification.type === 'make_offer') {
      // Marketplace offers without action required - show details only
      setDetailsNotification(notification);
      setShowDetailsModal(true);
    } else if (notification.type === 'offer_rejected') {
      setDetailsNotification(notification);
      setShowDetailsModal(true);
    }
  };

  const handleSelectNewCarrier = async () => {
    if (selectedNotification?.id) {
      try {
        const { acknowledgeRejectionNotification } = await import(
          '../../services/notificationFirestoreQueries'
        );
        await acknowledgeRejectionNotification(
          getFailoverAwareDatabase(),
          selectedNotification.id
        );
      } catch (err) {
        Logger.warn('[ShipperNotificationsTray] Failed to acknowledge rejection notification:', err as Error);
      }
    }
    const poNumber = String(
      selectedNotification?.poNumber ||
        selectedNotification?.loadDetails?.poNumber ||
        ''
    ).trim();
    const uid = currentUser?.uid;
    if (poNumber && uid) {
      try {
        const poData = await getShipperPurchaseOrderRecordForWorkflow(
          getFailoverAwareDatabase(),
          poNumber,
          uid
        );
        if (poData) {
          const poDocId = String((poData as { id?: string }).id || '').trim();
          const partnersPath = poDocId
            ? withAssignPoQuery('/shipper/partners', poDocId)
            : '/shipper/partners';
          navigate(partnersPath, {
            state: {
              poData,
              editingPO: poData,
              isEditing: true,
              rate: (poData as { rate?: number }).rate,
              fromRejection: true,
              rejectedLoadId: selectedNotification?.id,
            },
          });
          setShowRejectionModal(false);
          return;
        }
      } catch (err) {
        Logger.warn('[ShipperNotificationsTray] Failed to load PO for partner reassignment:', err as Error);
      }
    }
    if (selectedNotification?.loadDetails) {
      navigate('/shipper/partners', {
        state: {
          loadDetails: selectedNotification.loadDetails,
          poData: {
            poNumber,
            ...selectedNotification.loadDetails,
          },
          editingPO: {
            poNumber,
            ...selectedNotification.loadDetails,
          },
          isEditing: true,
          fromRejection: true,
          rejectedLoadId: selectedNotification.id,
        },
      });
    }
    setShowRejectionModal(false);
  };

  const handlePlaceInMarketplace = async () => {
    if (selectedNotification?.id) {
      try {
        const { acknowledgeRejectionNotification } = await import(
          '../../services/notificationFirestoreQueries'
        );
        await acknowledgeRejectionNotification(
          getFailoverAwareDatabase(),
          selectedNotification.id
        );
      } catch (err) {
        Logger.warn('[ShipperNotificationsTray] Failed to acknowledge rejection notification:', err as Error);
      }
    }
    if (selectedNotification?.loadDetails) {
      navigate('/shipper/marketplace', {
        state: {
          loadDetails: selectedNotification.loadDetails,
          fromRejection: true,
          rejectedLoadId: selectedNotification.id,
        },
      });
    }
    setShowRejectionModal(false);
  };

  const handleAcceptCounterOffer = async () => {
    if (selectedNotification?.id) {
      try {
        if (selectedNotification.loadDetails?.poNumber) {
          if (
            selectedNotification.type === 'make_offer' ||
            selectedNotification.type === 'carrier_counter_offer'
          ) {
            const carrierId = selectedNotification.carrierId;
            if (!carrierId || !currentUser?.uid) {
              throw new Error('Carrier ID or shipper session not found');
            }
            const offerRate = selectedNotification.loadDetails?.rate;
            if (!offerRate || offerRate <= 0) {
              throw new Error('Offer rate not found on notification');
            }
            const { deferCounterOfferAcceptance } = await import('../../services/deferredRateConfirmationService');
            await deferCounterOfferAcceptance({
              poNumber: selectedNotification.loadDetails.poNumber,
              carrierId,
              agreedRate: offerRate,
              partyId: currentUser.uid,
              partyRole: 'shipper',
            });
          } else if (selectedNotification.type === 'carrier_accept') {
            const carrierId = selectedNotification.carrierId;
            if (!carrierId || !currentUser?.uid) {
              throw new Error('Carrier ID or shipper session not found');
            }
            const poNumber = selectedNotification.loadDetails.poNumber;
            const poRecord = await getShipperPurchaseOrderRecordForWorkflow(
              getFailoverAwareDatabase(),
              poNumber,
              currentUser.uid
            );
            if (!poRecord) {
              throw new Error(`Purchase order not found for ${poNumber}`);
            }
            const poDocId = String(poRecord.id);
            const { requestCarrierRateConfirmation, buildPendingCarrierSnapshot } = await import(
              '../../services/deferredRateConfirmationService'
            );
            const { MigrationDataAccessLayer } = await import('../../services/migrationDataAccessLayer');
            const migrationDataAccess = new MigrationDataAccessLayer();
            const carrierResult = await migrationDataAccess.getUserProfile(carrierId);
            const carrierProfile = (carrierResult.success && carrierResult.data
              ? carrierResult.data
              : { id: carrierId }) as Record<string, unknown>;
            await requestCarrierRateConfirmation({
              poCollection: 'purchaseOrders',
              poDocId,
              poNumber,
              carrierId,
              pendingSnapshot: buildPendingCarrierSnapshot(carrierId, carrierProfile),
              partyId: currentUser.uid,
              partyRole: 'shipper',
            });
          }
        }
        
        // Action Required: delete after progressing accept (not mark-read alone).
        if (
          (DASHBOARD_ACTION_REQUIRED_NOTIFICATION_TYPES as readonly string[]).includes(
            selectedNotification.type
          )
        ) {
          const { acknowledgeRejectionNotification, acknowledgeRejectionNotificationsForPo } =
            await import('../../services/notificationFirestoreQueries');
          await acknowledgeRejectionNotification(
            getFailoverAwareDatabase(),
            selectedNotification.id
          );
          const poNumberForAck = selectedNotification.loadDetails?.poNumber;
          if (poNumberForAck && currentUser?.uid) {
            await acknowledgeRejectionNotificationsForPo(
              getFailoverAwareDatabase(),
              poNumberForAck,
              currentUser.uid
            );
          }
          setNotifications((prev) => prev.filter((n) => n.id !== selectedNotification.id));
        } else {
          const notificationRef = doc(
            getFailoverAwareDatabase(),
            'notifications',
            selectedNotification.id
          );
          await updateDoc(notificationRef, {
            read: true,
            status: NOTIFICATION_STATUS.ACCEPTED,
            requiresAction: false,
            updatedAt: serverTimestamp(),
          });
          setNotifications((prev) =>
            prev.map((n) => (n.id === selectedNotification.id ? { ...n, read: true } : n))
          );
        }
        
        // Counter-offer / carrier review acceptance defers assign until carrier signs rate confirmation
        setShowCounterOfferModal(false);
        
        if (
          selectedNotification.type === 'carrier_counter_offer' ||
          selectedNotification.type === 'carrier_accept' ||
          selectedNotification.type === 'make_offer'
        ) {
          alert('Carrier has been notified to review and accept the rate confirmation.');
        }
      } catch (error) {
        Logger.error('[ShipperNotificationsTray] Error accepting offer:', error as Error);
        alert('Failed to accept offer. Please try again.');
      }
    }
  };

  const handleRejectCounterOffer = async () => {
    if (!selectedNotification?.id || !currentUser?.uid) return;
    try {
      const poNumber =
        selectedNotification.poNumber || selectedNotification.loadDetails?.poNumber;
      if (!poNumber) {
        throw new Error('PO number not found on notification');
      }

      if (selectedNotification.type === NOTIFICATION_TYPES.MAKE_OFFER) {
        const carrierId = selectedNotification.carrierId;
        if (!carrierId) {
          throw new Error('Carrier ID not found in offer notification');
        }
        const offerRate = selectedNotification.loadDetails?.rate;
        if (!offerRate || offerRate <= 0) {
          throw new Error('Offer rate not found on notification');
        }

        const { rejectCarrierMakeOffer } = await import(
          '../../services/rejectCarrierMakeOfferService'
        );
        const result = await rejectCarrierMakeOffer({
          notificationId: selectedNotification.id,
          poNumber,
          carrierId,
          offerRate,
          partyId: currentUser.uid,
          partyRole: 'shipper',
        });
        if (!result.success) {
          throw new Error(result.error || 'Failed to reject carrier offer');
        }
      } else if (selectedNotification.type === NOTIFICATION_TYPES.CARRIER_COUNTER_OFFER) {
        const carrierId = selectedNotification.carrierId;
        if (!carrierId) {
          throw new Error('Carrier ID not found in counter offer');
        }
        const counterOfferRate = selectedNotification.loadDetails?.rate;
        if (!counterOfferRate || counterOfferRate <= 0) {
          throw new Error('Counter offer rate not found on notification');
        }

        const { rejectCarrierCounterOffer } = await import(
          '../../services/rejectCarrierCounterOfferService'
        );
        const result = await rejectCarrierCounterOffer({
          notificationId: selectedNotification.id,
          poNumber,
          carrierId,
          counterOfferRate,
          partyId: currentUser.uid,
          partyRole: 'shipper',
        });
        if (!result.success) {
          throw new Error(result.error || 'Failed to reject counter offer');
        }
      } else {
        return;
      }

      setNotifications((prev) =>
        prev.map((n) => (n.id === selectedNotification.id ? { ...n, read: true } : n))
      );
      setShowCounterOfferModal(false);
      setSelectedNotification(null);
    } catch (error) {
      Logger.error('[ShipperNotificationsTray] Error rejecting offer:', error as Error);
      alert('Failed to reject offer. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className={styles.panel} ref={trayRef}>
        <div className={styles.header}>
          <h3>Notifications</h3>
          <button type="button" onClick={onClose} className={styles.closeButton} aria-label="Close notifications">×</button>
        </div>
        <div className={styles.loading}>Loading notifications...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.panel} ref={trayRef}>
        <div className={styles.header}>
          <h3>Notifications</h3>
          <button type="button" onClick={onClose} className={styles.closeButton} aria-label="Close notifications">×</button>
        </div>
        <div className={styles.error}>
          <span>Error: {error}</span>
          <button type="button" onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        ref={trayRef}
        className={styles.panel}
        onWheel={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h3>Notifications</h3>
          <button type="button" onClick={onClose} className={styles.closeButton} aria-label="Close notifications">×</button>
        </div>
        <div className={styles.notificationList}>
        {notifications.length === 0 ? (
          <div className={styles.noNotifications}>
            <div>No notifications</div>
            <div>You're all caught up!</div>
          </div>
        ) : (
          <>
          {notifications.slice(0, visibleCount).map((n) => {
            const amount = n.loadDetails?.rate;
            const amountLabel = typeof amount === 'number' ? `$${amount.toLocaleString()}` : undefined;
            const po = n.loadDetails?.poNumber || '—';
            const created = n.createdAt?.toLocaleString ? n.createdAt.toLocaleString() : '';

            const badgeClass = n.type === 'make_offer'
              ? styles.typeOffer
              : n.type === 'carrier_counter_offer'
              ? styles.typeCounter
              : (n.type || '').includes('accept')
              ? styles.typeAccepted
              : (n.type || '').includes('reject')
              ? styles.typeRejected
              : styles.typeOffer;

            return (
              <div key={n.id} className={styles.card} onClick={() => handleNotificationClick(n)}>
                <div className={styles.cardHeader}>
                  <span className={`${styles.typeBadge} ${badgeClass}`}>{(n.type || 'notification').replace(/_/g,' ')}</span>
                </div>
                <div className={styles.actions}>
                  {!n.read && (
                    <button type="button" className={styles.btnMarkRead} onClick={(e) => { e.stopPropagation(); handleMarkAsRead(n.id); }}>Mark read</button>
                  )}
                  <button type="button" className={styles.btnLink} onClick={(e) => { e.stopPropagation(); setDetailsNotification(n); setShowDetailsModal(true); }}>Details</button>
                </div>
                <div className={styles.messageRow}>
                  <span>{n.message}</span>
                </div>
                <div className={styles.metaRow}>
                  <span>{created}</span>
                  {amountLabel && <span>Amount: <span className={styles.amount}>{amountLabel}</span></span>}
                  <span>PO: {po}</span>
                </div>
              </div>
            );
          })}
          {hasMore && (
            <div className={styles.loadMoreRow}>
              <button type="button" className={styles.loadMoreButton} onClick={loadMore}>
                {NOTIFICATION_TRAY_LOAD_MORE_LABEL}
                {remaining > 0 ? ` (${Math.min(remaining, pageSize)} of ${remaining} left)` : ''}
              </button>
            </div>
          )}
          </>
        )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && detailsNotification && (
        <Dialog isOpen={showDetailsModal} onDismiss={() => setShowDetailsModal(false)}>
          <div style={{ padding: '20px', maxWidth: '500px' }}>
            <h2 style={{ marginBottom: '16px', color: '#333' }}>Notification Details</h2>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>Message:</div>
              <div style={{ marginBottom: '16px' }}>{detailsNotification.message}</div>
              
              {detailsNotification.loadDetails && (
                <>
                  <div style={{ fontWeight: 600, marginBottom: '8px' }}>Load Details:</div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>PO Number:</strong> {detailsNotification.loadDetails.poNumber}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Rate:</strong> ${detailsNotification.loadDetails.rate?.toLocaleString()}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Pickup:</strong> {[detailsNotification.loadDetails.pickupLocation?.address, detailsNotification.loadDetails.pickupLocation?.city, detailsNotification.loadDetails.pickupLocation?.state].filter(Boolean).join(', ')}
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <strong>Delivery:</strong> {[detailsNotification.loadDetails.deliveryLocation?.address, detailsNotification.loadDetails.deliveryLocation?.city, detailsNotification.loadDetails.deliveryLocation?.state].filter(Boolean).join(', ')}
                  </div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 18px', flex: 1, cursor: 'pointer', ...CARRIER_ACTION_BUTTON_TEXT_STYLE }} onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && selectedNotification && (
        <RejectionOptionsModal
          isOpen={showRejectionModal}
          onClose={() => setShowRejectionModal(false)}
          onSelectNewCarrier={handleSelectNewCarrier}
          onPlaceInMarketplace={handlePlaceInMarketplace}
          poNumber={selectedNotification.loadDetails?.poNumber || 'Unknown'}
        />
      )}

      {/* Counter Offer Modal */}
      {showCounterOfferModal && selectedNotification && selectedNotification.loadDetails && (
        <CounterOfferModal
          isOpen={showCounterOfferModal}
          onClose={() => setShowCounterOfferModal(false)}
          onAccept={handleAcceptCounterOffer}
          onReject={handleRejectCounterOffer}
          currentRate={selectedNotification.loadDetails.rate}
          counterOffer={selectedNotification.loadDetails.rate}
          loadDetails={{
            ...selectedNotification.loadDetails,
            carrierName: selectedNotification.senderName || 'Unknown Carrier'
          }}
        />
      )}
    </>
  );
};

export default ShipperNotificationsTray;
